import { defineStore } from 'pinia';
import { ref } from 'vue';

import { TipTapContent } from '@/components/canvas/docxContent';
import {
    createDocument,
    deleteDocument,
    getDocuments,
    updateDocument
} from '@/services/documents';
import { DocumentItem } from '@/types';

export const useDocumentsStore = defineStore('documents', () => {
    const documents = ref<DocumentItem[]>([]);
    const shouldScrollToTop = ref(false);

    async function fetchDocumentsInStore(tenantId: string) {
        const docs = await getDocuments(tenantId);
        if (docs) {
            const sortedDocs = [...docs].sort((a, b) => {
                return (
                    new Date(b.updated_at).getTime() -
                    new Date(a.updated_at).getTime()
                );
            });
            documents.value = sortedDocs;
        } else {
            documents.value = [];
        }
    }

    async function addDocument(
        name: string,
        tenantId: string,
        documentType: string,
        content?: string,
        referrerId?: string,
        chatSessionId?: string,
        docContent?: TipTapContent
    ) {
        const response = await createDocument(
            tenantId,
            documentType,
            name,
            content,
            referrerId,
            chatSessionId,
            docContent
        );
        if (!response) return;

        upsertDocumentInStore(response);
        return response;
    }

    async function updateDocumentAndStore(
        documentId: string,
        tenantId: string,
        name?: string,
        referrerId?: string
    ) {
        const updatedDocument = await updateDocument(
            documentId,
            tenantId,
            name,
            referrerId
        );
        if (!updatedDocument) return;

        replaceAndMoveToTop(documentId, updatedDocument);
    }

    async function archiveDocument(documentId: string, tenantId: string) {
        try {
            await deleteDocument(documentId, tenantId);

            const index = documents.value.findIndex(
                (doc) => doc.id === documentId
            );
            if (index !== -1) {
                documents.value.splice(index, 1);
            }
            return { success: true };
        } catch (error) {
            console.error('Failed to delete document:', error);
        }
    }

    function moveDocumentToTop() {
        shouldScrollToTop.value = true;
    }

    function replaceAndMoveToTop(
        documentId: string,
        document: DocumentItem
    ): boolean {
        const index = documents.value.findIndex((doc) => doc.id === documentId);
        if (index === -1) return false;

        documents.value[index] = document;
        const [existing] = documents.value.splice(index, 1);
        documents.value.unshift(existing);
        moveDocumentToTop();
        return true;
    }

    function upsertDocumentInStore(document: DocumentItem) {
        if (!replaceAndMoveToTop(document.id, document)) {
            documents.value.unshift(document);
            moveDocumentToTop();
        }
    }

    return {
        documents,
        fetchDocumentsInStore,
        updateDocumentAndStore,
        moveDocumentToTop,
        archiveDocument,
        addDocument,
        upsertDocumentInStore,
        shouldScrollToTop
    };
});
